/**
 * Tenant Settings - Page centrale des paramètres du tenant
 * ISO 27001 A.9.2.1 - User registration and de-registration
 * ISO 27001 A.9.2.3 - Management of privileged access rights
 *
 * Cette page regroupe tous les paramètres administratifs du tenant:
 * - Sociétés (CompaniesManagement)
 * - Utilisateurs (UsersManagement)
 * - Sécurité (SecuritySettings)
 * - Audit (AuditLogsManagement)
 * - Abonnement (lien vers billing)
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  Users,
  Shield,
  FileText,
  CreditCard,
  Settings,
  ChevronRight,
  Lock,
} from 'lucide-react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';

interface TabItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  description: string;
}

export const TenantSettings: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();

  const TENANT_TABS: TabItem[] = [
    {
      id: 'companies',
      label: t('admin.tenantSettings.tabs.companies'),
      icon: Building2,
      path: '/settings/tenant/companies',
      description: t('admin.tenantSettings.tabs.companiesDesc'),
    },
    {
      id: 'users',
      label: t('admin.tenantSettings.tabs.users'),
      icon: Users,
      path: '/settings/tenant/users',
      description: t('admin.tenantSettings.tabs.usersDesc'),
    },
    {
      id: 'security',
      label: t('admin.tenantSettings.tabs.security'),
      icon: Shield,
      path: '/settings/tenant/security',
      description: t('admin.tenantSettings.tabs.securityDesc'),
    },
    {
      id: 'permissions',
      label: t('admin.tenantSettings.tabs.permissions'),
      icon: Lock,
      path: '/settings/tenant/permissions',
      description: t('admin.tenantSettings.tabs.permissionsDesc'),
    },
    {
      id: 'audit',
      label: t('admin.tenantSettings.tabs.audit'),
      icon: FileText,
      path: '/settings/tenant/audit',
      description: t('admin.tenantSettings.tabs.auditDesc'),
    },
    {
      id: 'billing',
      label: t('admin.tenantSettings.tabs.billing'),
      icon: CreditCard,
      path: '/settings/tenant/billing',
      description: t('admin.tenantSettings.tabs.billingDesc'),
    },
  ];

  // Déterminer l'onglet actif basé sur l'URL
  const activeTab = TENANT_TABS.find((tab) => location.pathname.startsWith(tab.path))?.id || 'companies';

  // Vérifier si l'utilisateur a les droits d'admin tenant
  const isTenantAdmin = user?.role === 'OWNER' || user?.role === 'ADMIN';

  if (!isTenantAdmin) {
    return (
      <div className="px-8 py-12">
        <div className="bg-red-50/50 backdrop-blur-sm border border-red-200/50 rounded-2xl p-8 text-center">
          <Shield className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-red-900 mb-2">{t('admin.tenantSettings.restrictedAccess')}</h2>
          <p className="text-red-700">
            {t('admin.tenantSettings.restrictedMessage')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="px-8 pt-8 pb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-teal-100 rounded-2xl">
            <Settings className="w-8 h-8 text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('admin.tenantSettings.title')}</h1>
            <p className="text-gray-500 mt-0.5">
              {t('admin.tenantSettings.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="px-8 pb-6">
        <div className="bg-white/20 backdrop-blur-xl rounded-2xl border border-white/30 p-2">
          <nav className="flex gap-2 overflow-x-auto">
            {TENANT_TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => navigate(tab.path)}
                  className={`
                    flex items-center gap-2 px-4 py-3 rounded-xl whitespace-nowrap
                    transition-all duration-300 min-w-fit
                    ${
                      isActive
                        ? 'bg-white/60 text-teal-700 font-semibold shadow-lg border border-white/50'
                        : 'text-gray-600 hover:bg-white/30 hover:text-gray-900'
                    }
                  `}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-teal-600' : ''}`} />
                  <span className="text-sm">{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content Area - Outlet pour les sous-routes */}
      <div className="px-8 pb-12">
        <Outlet />
      </div>
    </div>
  );
};

/**
 * Composant pour la page d'index /settings/tenant (sans sous-route)
 * Affiche une vue d'ensemble avec des cartes cliquables
 */
export const TenantSettingsIndex: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const TENANT_TABS: TabItem[] = [
    {
      id: 'companies',
      label: t('admin.tenantSettings.tabs.companies'),
      icon: Building2,
      path: '/settings/tenant/companies',
      description: t('admin.tenantSettings.tabs.companiesDesc'),
    },
    {
      id: 'users',
      label: t('admin.tenantSettings.tabs.users'),
      icon: Users,
      path: '/settings/tenant/users',
      description: t('admin.tenantSettings.tabs.usersDesc'),
    },
    {
      id: 'security',
      label: t('admin.tenantSettings.tabs.security'),
      icon: Shield,
      path: '/settings/tenant/security',
      description: t('admin.tenantSettings.tabs.securityDesc'),
    },
    {
      id: 'permissions',
      label: t('admin.tenantSettings.tabs.permissions'),
      icon: Lock,
      path: '/settings/tenant/permissions',
      description: t('admin.tenantSettings.tabs.permissionsDesc'),
    },
    {
      id: 'audit',
      label: t('admin.tenantSettings.tabs.audit'),
      icon: FileText,
      path: '/settings/tenant/audit',
      description: t('admin.tenantSettings.tabs.auditDesc'),
    },
    {
      id: 'billing',
      label: t('admin.tenantSettings.tabs.billing'),
      icon: CreditCard,
      path: '/settings/tenant/billing',
      description: t('admin.tenantSettings.tabs.billingDesc'),
    },
  ];

  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1
    transition-all duration-500 cursor-pointer group`;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {TENANT_TABS.map((tab) => {
        const Icon = tab.icon;

        return (
          <div
            key={tab.id}
            onClick={() => navigate(tab.path)}
            className={cardClasses}
          >
            <div className="flex items-start justify-between">
              <div className="p-3 bg-teal-100 rounded-2xl group-hover:bg-teal-200 transition-colors">
                <Icon className="w-6 h-6 text-teal-600" />
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-teal-600 group-hover:translate-x-1 transition-all" />
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mt-4">{tab.label}</h3>
            <p className="text-sm text-gray-500 mt-1">{tab.description}</p>
          </div>
        );
      })}
    </div>
  );
};

export default TenantSettings;
